/**
 * Shared Merchant Credit/Debit Notes tab — used identically on both
 * Admin -> Vendor Commissions and Admin -> Commission Invoices so the
 * two pages never show divergent data (same `vendor_commission_credit_notes`
 * backend collection, same list/preview/download/bulk-download behaviour).
 */
import { useState, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../ui/sonner';
import { FileText, Download, Loader2 } from 'lucide-react';

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function monthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(parseInt(y), parseInt(mo) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function CreditNoteCard({ cn, onPreview, onDownload, selected, onToggleSelect }) {
  const [downloading, setDownloading] = useState(false);
  const isDebit = cn.document_type === 'debit_note';
  async function handleDownload() {
    setDownloading(true);
    await onDownload(cn.id, cn.credit_note_number);
    setDownloading(false);
  }
  return (
    <div
      className={`border rounded-xl bg-white overflow-hidden ${isDebit ? 'border-emerald-100' : 'border-orange-100'}`}
      data-testid={`credit-note-card-${cn.id}`}
    >
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
        <Checkbox
          checked={!!selected}
          onCheckedChange={() => onToggleSelect(cn.id)}
          className="mt-1"
          data-testid={`select-credit-note-${cn.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-gray-900" data-testid="credit-note-number">
              {cn.credit_note_number}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isDebit ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {isDebit ? 'Debit' : 'Credit'}
            </span>
            {cn.context === 'manual_admin_created' && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">
                Manually Created
              </span>
            )}
            {cn.original_invoice_late_declared && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 font-medium">
                Original Late-Declared
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5">
            Against invoice <span className="font-mono">{cn.original_invoice_number}</span>
            <span className="text-gray-400 ml-2 text-xs">({monthLabel(cn.original_month)})</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Issued {monthLabel(cn.month)} · Order #{(cn.order_id || '').slice(0, 8)}
          </p>
          {cn.reason && <p className="text-xs text-gray-400 mt-1">{cn.reason}</p>}
        </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">{isDebit ? 'Additional Due' : 'Credit Amount'}</p>
          <p className={`text-lg font-bold ${isDebit ? 'text-emerald-700' : 'text-red-700'}`} data-testid="credit-note-total">
            {isDebit ? '+' : '-'}{fmt(Math.abs(cn.grand_total))}
          </p>
        </div>
      </div>

      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => onPreview(cn.id)} data-testid="preview-credit-note-btn">
          <FileText className="h-3 w-3 mr-1" />
          Preview
        </Button>
        <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleDownload} disabled={downloading} data-testid="download-credit-note-btn">
          {downloading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
          Download
        </Button>
      </div>
    </div>
  );
}

export function CommissionCreditNotesTab({ vendorId = null, reloadSignal = 0 }) {
  const { apiCall } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const allSelected = creditNotes.length > 0 && selectedIds.length === creditNotes.length;
  const someSelected = selectedIds.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = vendorId ? `?vendor_id=${vendorId}` : '';
      const data = await apiCall(`/api/admin/commission-invoices/credit-notes${params}`);
      setCreditNotes(Array.isArray(data) ? data : []);
    } catch {
      // non-blocking
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load, reloadSignal]);

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleSelectAll() {
    setSelectedIds(allSelected ? [] : creditNotes.map(c => c.id));
  }
  function clearSelection() {
    setSelectedIds([]);
  }

  async function previewCreditNote(id) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/credit-notes/${id}/html`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to load credit note preview');
    }
  }

  async function downloadCreditNotePdf(id, number) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/credit-notes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download credit note PDF');
    }
  }

  async function bulkDownloadSelected() {
    setBulkDownloading(true);
    const targets = creditNotes.filter(c => selectedIds.includes(c.id));
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const zip = new JSZip();
    let ok = 0;
    for (const cn of targets) {
      try {
        const response = await fetch(`${BACKEND}/api/admin/commission-invoices/credit-notes/${cn.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        zip.file(`${cn.credit_note_number || cn.id}.pdf`, blob);
        ok++;
      } catch {
        // continue — one failed note shouldn't block the rest
      }
    }
    if (ok > 0) {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit-debit-notes-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ok} note PDF${ok !== 1 ? 's' : ''} as a ZIP`);
    } else {
      toast.error('Failed to download any notes');
    }
    setBulkDownloading(false);
    clearSelection();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (creditNotes.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400" data-testid="credit-notes-empty">
        <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium text-gray-500">No credit notes issued</p>
        <p className="text-xs mt-1">Issued automatically for cross-month order returns/cancellations, or manually via "Create Document".</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 mb-3" data-testid="cn-bulk-action-bar">
        <div className="flex items-center gap-2">
          <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-credit-notes" />
          <span className="text-xs font-medium text-gray-600">
            {someSelected ? `${selectedIds.length} of ${creditNotes.length} selected` : 'Select all'}
          </span>
        </div>
        {someSelected && (
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={bulkDownloadSelected} disabled={bulkDownloading} data-testid="cn-bulk-download-btn">
              {bulkDownloading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
              Download {selectedIds.length} as ZIP
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-500" onClick={clearSelection} data-testid="cn-bulk-clear-btn">
              Clear
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="credit-notes-list">
        {creditNotes.map(cn => (
          <CreditNoteCard
            key={cn.id}
            cn={cn}
            onPreview={previewCreditNote}
            onDownload={downloadCreditNotePdf}
            selected={selectedIds.includes(cn.id)}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>
    </>
  );
}

export default CommissionCreditNotesTab;
